import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  MessageCircle, X, Send, Loader2, Headphones, Sparkles, ExternalLink, RefreshCcw, User,
} from "lucide-react";
import { API, USER_TOKEN, userApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TELEGRAM_FALLBACK = "rupeetethersupport";

/**
 * Floating support widget. Visible on authenticated user pages only;
 * streams responses from /api/support/chat via Server-Sent Events and
 * offers a one-click Telegram handoff.
 */
export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState("");
  const bodyRef = useRef(null);
  const esRef = useRef(null);

  const configQ = useQuery({
    queryKey: ["support-config"],
    queryFn: () => userApi.get("/support/config").then((r) => r.data),
    staleTime: 10 * 60 * 1000,
  });
  const telegramHandle = configQ.data?.telegram || TELEGRAM_FALLBACK;
  const telegramUrl = configQ.data?.telegram_url || `https://t.me/${TELEGRAM_FALLBACK}`;

  const loadHistory = async () => {
    try {
      const r = await userApi.get("/support/history");
      if (r.data.session_id) setSessionId(r.data.session_id);
      setMessages(r.data.messages || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (open) {
      loadHistory();
      setTimeout(() => bodyRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 9e9, behavior: "smooth" });
  }, [messages, streaming]);

  // Clean up any in-flight stream on unmount / widget close
  useEffect(() => {
    return () => esRef.current?.abort?.();
  }, []);

  const quickPrompts = [
    "What's the status of my last deposit?",
    "How long does verification take?",
    "Show me today's INR bonus rules.",
    "How do I sell USDT?",
  ];

  const stopStreaming = () => {
    if (esRef.current) {
      try { esRef.current.abort(); } catch { /* noop */ }
      esRef.current = null;
    }
  };

  const resetConversation = async () => {
    stopStreaming();
    try { await userApi.post("/support/new"); } catch { /* ignore */ }
    setSessionId(null);
    setMessages([]);
    setStreaming("");
    toast.success("Started a new conversation");
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    const userMessage = { role: "user", content: msg, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setStreaming("");

    const ctrl = new AbortController();
    esRef.current = ctrl;
    try {
      const token = localStorage.getItem(USER_TOKEN);
      const resp = await fetch(`${API}/support/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
        signal: ctrl.signal,
      });

      if (!resp.ok || !resp.body) {
        let detail = "Could not reach the support team. Please try again.";
        try { detail = (await resp.json())?.detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      // basic SSE line parser
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const chunk of parts) {
          let evt = "message";
          const dataLines = [];
          for (const line of chunk.split("\n")) {
            if (line.startsWith("event:")) evt = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let payload = null;
          try { payload = JSON.parse(dataLines.join("\n")); } catch { /* ignore */ }
          if (evt === "session" && payload?.session_id) setSessionId(payload.session_id);
          else if (evt === "message" && payload?.delta) {
            acc += payload.delta;
            setStreaming(acc);
          }
        }
      }
      const assistantMsg = { role: "assistant", content: acc, created_at: new Date().toISOString() };
      setMessages((prev) => [...prev, assistantMsg]);
      setStreaming("");
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(e.message || "Chat failed");
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `We couldn't reach the support team. For urgent help, message us on Telegram @${telegramHandle}.`,
            created_at: new Date().toISOString(),
          },
        ]);
      }
    } finally {
      esRef.current = null;
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        className={cn(
          "fixed bottom-20 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-teal-800 text-white shadow-[0_14px_36px_hsl(186_60%_18%/0.4)] transition-transform hover:scale-105 md:bottom-6 md:right-6",
          open && "scale-90 opacity-0 pointer-events-none"
        )}
        onClick={() => setOpen(true)}
        aria-label="Open support chat"
        data-testid="support-widget-launcher"
      >
        <MessageCircle className="h-6 w-6" />
        <span className="absolute -right-1 -top-1 flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-300 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-400" />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-2 bottom-2 z-50 flex max-h-[90vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_30px_80px_hsl(222_35%_12%/0.3)] sm:inset-x-auto sm:bottom-4 sm:right-4 sm:h-[560px] sm:w-[380px]"
            data-testid="support-widget-panel"
            role="dialog"
            aria-label="Support chat"
          >
            {/* Header */}
            <div className="flex items-center gap-3 bg-gradient-to-r from-primary via-teal-700 to-teal-900 px-4 py-3 text-white">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
                <Headphones className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Support team</p>
                <p className="flex items-center gap-1 text-[11px] opacity-85">
                  <Sparkles className="h-3 w-3" /> We reply fast and verify every payment within 15 minutes
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/15"
                onClick={resetConversation}
                aria-label="Start new conversation"
                data-testid="support-widget-reset"
              >
                <RefreshCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/15"
                onClick={() => setOpen(false)}
                aria-label="Close support chat"
                data-testid="support-widget-close"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Messages */}
            <div
              ref={bodyRef}
              className="flex-1 space-y-3 overflow-y-auto scrollbar-thin bg-slate-50/60 p-4"
              data-testid="support-widget-messages"
            >
              {messages.length === 0 && !streaming && (
                <div className="space-y-3">
                  <div className="rounded-xl border bg-white p-3 text-sm text-foreground shadow-sm">
                    <p className="font-medium">Hi 👋</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Ask us about your deposits, exchange rates, bonuses or sell orders. For disputes or stuck payouts, we'll
                      hand you to our team on Telegram.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {quickPrompts.map((p) => (
                      <button
                        key={p}
                        onClick={() => send(p)}
                        className="rounded-lg border bg-white px-3 py-2 text-left text-xs text-foreground transition-colors hover:border-primary hover:bg-primary/5"
                        data-testid="support-widget-quick-prompt"
                        disabled={sending}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <Message key={i} role={m.role} content={m.content} />
              ))}
              {streaming && <Message role="assistant" content={streaming} streaming />}
            </div>

            {/* Telegram handoff banner */}
            <div className="flex items-center gap-2 border-t bg-amber-50 px-4 py-2 text-xs text-amber-900">
              <ExternalLink className="h-3.5 w-3.5" />
              <span className="flex-1 truncate">
                Need a human? Message{" "}
                <a
                  href={telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline"
                  data-testid="support-widget-telegram-link"
                >
                  @{telegramHandle}
                </a>{" "}
                on Telegram.
              </span>
            </div>

            {/* Composer */}
            <form
              className="flex items-end gap-2 border-t bg-white p-3"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <textarea
                className="min-h-[42px] flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="Ask about a deposit, rate or payout\u2026"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                data-testid="support-widget-input"
                disabled={sending}
                maxLength={1500}
              />
              <Button
                type="submit"
                size="icon"
                className="h-11 w-11 shrink-0"
                disabled={sending || !input.trim()}
                data-testid="support-widget-send"
                aria-label="Send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Message({ role, content, streaming }) {
  const mine = role === "user";
  return (
    <div
      className={cn("flex items-end gap-2", mine ? "flex-row-reverse" : "")}
      data-testid={`support-widget-msg-${role}`}
    >
      <div
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white",
          mine ? "bg-slate-700" : "bg-primary"
        )}
      >
        {mine ? <User className="h-3.5 w-3.5" /> : <Headphones className="h-3.5 w-3.5" />}
      </div>
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm shadow-sm",
          mine
            ? "rounded-br-sm bg-primary text-primary-foreground"
            : "rounded-bl-sm bg-white text-foreground"
        )}
      >
        {content}
        {streaming && <span className="ml-1 inline-block h-3 w-1 animate-pulse bg-current align-baseline" />}
      </div>
    </div>
  );
}
